import { zodResolver } from '@hookform/resolvers/zod';
import type {
  Branch,
  Category,
  CustomFieldType,
  Item,
  Shelf,
} from '@packages/shared-types';
import {
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  LayoutGrid,
  Loader2,
  MoreHorizontal,
  Move,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  type DragEndEvent,
  type DragStartEvent,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { ProductStatusBadge } from '@/components/shared/ProductStatusBadge';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { getItemDetectionStatus } from '@/utils/detectionStatus';
import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getNextCategoryCode,
  type CreateCategoryPayload,
} from '@/services/categoryService';
import {
  getFoldersTree,
  createFolder,
  updateFolder,
  deleteFolder,
  getNextFolderCode,
  type FolderTreeNode,
  type CreateFolderPayload,
} from '@/services/folderService';
import { listItems, updateItem } from '@/services/itemService';
import { listBranches } from '@/services/branchService';
import { listShelves } from '@/services/shelfService';
import {
  createCustomFieldDefinition,
} from '@/services/customFieldService';
import { useAuth } from '@/hooks/useAuth';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';

const categoryFormSchema = z.object({
  name: z.string().min(1, 'pages.categories.form.nameRequired'),
  folderId: z.string().uuid().optional().or(z.literal('')),
  code: z
    .string()
    .optional()
    .refine((v) => !v || /^[0-9A-Za-z]{3}$/.test(v), {
      message: 'pages.categories.form.codeInvalid',
    }),
});

const folderFormSchema = z.object({
  name: z.string().min(1, 'pages.folders.form.nameRequired'),
  parentFolderId: z.string().uuid().optional().or(z.literal('')),
  code: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{3}$/.test(v), {
      message: 'pages.folders.form.codeInvalid',
    }),
});

const createCfFormSchema = z.object({
  name: z.string().min(1, 'pages.customFields.form.nameRequired'),
  key: z.string().min(1, 'pages.customFields.form.keyRequired'),
  type: z.enum(['text', 'textarea', 'number', 'url', 'options']),
  required: z.boolean().optional(),
  options: z.string().optional(),
});

type CategoryFormValues = z.infer<typeof categoryFormSchema>;
type PendingCustomFieldDef = {
  name: string;
  key: string;
  type: CustomFieldType;
  required: boolean;
  options?: string[];
};

type TreeNode =
  | { type: 'folder'; id: string; name: string; code: string; children: TreeNode[] }
  | { type: 'category'; id: string; name: string; code: string; categoryId: string };

type SelectedNode = { type: 'folder'; id: string } | { type: 'category'; id: string };

function buildTree(
  folders: FolderTreeNode[],
  categories: Category[],
): TreeNode[] {
  const rootCategories = categories.filter((c) => !c.folderId);
  const result: TreeNode[] = [];

  function attachCategoriesToFolder(
    folderNode: FolderTreeNode,
  ): TreeNode[] {
    const folderCats = categories.filter(
      (c) => c.folderId === folderNode.id,
    );
    const categoryNodes: TreeNode[] = folderCats.map((c) => ({
      type: 'category' as const,
      id: `cat-${c.id}`,
      name: c.name,
      code: c.code,
      categoryId: c.id,
    }));
    const childFolderNodes = (folderNode.children || []).flatMap(
      (child) => attachCategoriesToFolder(child),
    );
    return [
      {
        type: 'folder',
        id: folderNode.id,
        name: folderNode.name,
        code: folderNode.code,
        children: [...childFolderNodes, ...categoryNodes],
      },
    ];
  }

  for (const f of folders) {
    result.push(...attachCategoriesToFolder(f));
  }

  for (const c of rootCategories) {
    result.push({
      type: 'category',
      id: `cat-${c.id}`,
      name: c.name,
      code: c.code,
      categoryId: c.id,
    });
  }

  return result;
}

const flattenFolders = (nodes: FolderTreeNode[]): FolderTreeNode[] =>
  nodes.flatMap((n) => [n, ...flattenFolders(n.children || [])]);

type BreadcrumbSegment =
  | { type: 'root'; id: null; name: string }
  | { type: 'folder'; id: string; name: string }
  | { type: 'category'; id: string; name: string };

function getBreadcrumbPath(
  selectedNode: SelectedNode,
  folders: FolderTreeNode[],
  categories: Category[],
  rootLabel: string,
): BreadcrumbSegment[] {
  const flat = flattenFolders(folders);
  if (selectedNode.type === 'folder') {
    const folder = flat.find((f) => f.id === selectedNode.id);
    if (!folder) return [{ type: 'root', id: null, name: rootLabel }];
    const path: BreadcrumbSegment[] = [
      { type: 'folder', id: folder.id, name: folder.name },
    ];
    let current: FolderTreeNode | undefined = folder;
    while (current?.parentFolderId) {
      const parent = flat.find((f) => f.id === current!.parentFolderId);
      if (parent) {
        path.unshift({ type: 'folder', id: parent.id, name: parent.name });
        current = parent;
      } else {
        break;
      }
    }
    path.unshift({ type: 'root', id: null, name: rootLabel });
    return path;
  }
  const category = categories.find((c) => c.id === selectedNode.id);
  if (!category)
    return [{ type: 'root', id: null, name: rootLabel }];
  if (category.folderId) {
    const folderPath = getBreadcrumbPath(
      { type: 'folder', id: category.folderId },
      folders,
      categories,
      rootLabel,
    );
    return [
      ...folderPath,
      { type: 'category', id: category.id, name: category.name },
    ];
  }
  return [
    { type: 'root', id: null, name: rootLabel },
    { type: 'category', id: category.id, name: category.name },
  ];
}

const FOLDER_PARAM = 'folder';
const CATEGORY_PARAM = 'category';

export function CategoriesPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'root' || user?.role === 'admin';
  const { activeTenant } = useTenantSwitcher();
  const foldersEnabled =
    (activeTenant?.settings as { foldersEnabled?: boolean } | undefined)
      ?.foldersEnabled ?? false;
  const title = useIndustryTranslation('pages.categories.title');
  const description = useIndustryTranslation('pages.categories.description');
  const createLabel = useIndustryTranslation('pages.categories.actions.create');
  const emptyLabel = useIndustryTranslation('pages.categories.list.empty');
  const loadingLabel = useIndustryTranslation('pages.categories.list.loading');
  const successCreate = useIndustryTranslation('pages.categories.success.create');
  const errorLoad = useIndustryTranslation('pages.categories.error.load');
  const errorCreate = useIndustryTranslation('pages.categories.error.create');
  const successDeleteCategory = useIndustryTranslation('pages.categories.success.delete');
  const errorDeleteCategory = useIndustryTranslation('pages.categories.error.delete');
  const nameTableLabel = useIndustryTranslation('pages.items.table.name');
  const skuTableLabel = useIndustryTranslation('pages.items.table.sku');
  const branchTableLabel = useIndustryTranslation('pages.items.table.branch');
  const shelfTableLabel = useIndustryTranslation('pages.items.table.shelf');
  const statusTableLabel = useIndustryTranslation('pages.items.table.status');
  const statusDetectedLabel = useIndustryTranslation('pages.items.status.detected');
  const statusNotSeenRecentlyLabel = useIndustryTranslation(
    'pages.items.status.notSeenRecently',
  );
  const statusMissingLabel = useIndustryTranslation('pages.items.status.missing');

  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [folders, setFolders] = useState<FolderTreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(
    new Set(),
  );

  const selectNode = useCallback(
    (node: SelectedNode | null) => {
      setSelectedNode(node);
      const next = new URLSearchParams(searchParams);
      next.delete(FOLDER_PARAM);
      next.delete(CATEGORY_PARAM);
      if (node) {
        if (node.type === 'folder') {
          next.set(FOLDER_PARAM, node.id);
        } else {
          next.set(CATEGORY_PARAM, node.id);
        }
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!folders.length && !categories.length) return;
    const folderId = searchParams.get(FOLDER_PARAM);
    const categoryId = searchParams.get(CATEGORY_PARAM);
    if (categoryId) {
      const cat = categories.find((c) => c.id === categoryId);
      if (cat) {
        setSelectedNode({ type: 'category', id: categoryId });
        if (cat.folderId) {
          const flat = flattenFolders(folders);
          const folder = flat.find((f) => f.id === cat.folderId);
          if (folder) {
            const toExpand = new Set<string>();
            let current: FolderTreeNode | undefined = folder;
            while (current) {
              toExpand.add(current.id);
              current = current.parentFolderId
                ? flat.find((f) => f.id === current!.parentFolderId)
                : undefined;
            }
            setExpandedFolderIds((prev) => new Set([...prev, ...toExpand]));
          }
        }
        return;
      }
    }
    if (folderId) {
      const flat = flattenFolders(folders);
      const folder = flat.find((f) => f.id === folderId);
      if (folder) {
        setSelectedNode({ type: 'folder', id: folderId });
        const toExpand = new Set<string>();
        let current: FolderTreeNode | undefined = folder;
        while (current) {
          toExpand.add(current.id);
          current = current.parentFolderId
            ? flat.find((f) => f.id === current!.parentFolderId)
            : undefined;
        }
        setExpandedFolderIds((prev) => new Set([...prev, ...toExpand]));
        return;
      }
    }
    setSelectedNode(null);
  }, [
    searchParams,
    folders,
    categories,
  ]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<FolderTreeNode | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<FolderTreeNode | null>(null);
  const [deletingFolderAction, setDeletingFolderAction] = useState<'move' | 'delete' | null>(null);
  const [deleteMoveToFolderId, setDeleteMoveToFolderId] = useState<string>('');
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [movingNode, setMovingNode] = useState<{
    type: 'category' | 'folder';
    id: string;
    name: string;
  } | null>(null);
  const [moveDestinationFolderId, setMoveDestinationFolderId] =
    useState<string>('');
  const [dndPendingMove, setDndPendingMove] = useState<{
    item: { type: 'category' | 'folder'; id: string; name: string };
    destinationId: string;
    destinationName: string;
  } | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [isDndConfirming, setIsDndConfirming] = useState(false);
  const [movingItem, setMovingItem] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [moveDestinationCategoryId, setMoveDestinationCategoryId] =
    useState<string>('');
  const [productMovePending, setProductMovePending] = useState<{
    itemId: string;
    folderId: string;
    folderName: string;
  } | null>(null);
  const [createPendingCustomFields, setCreatePendingCustomFields] = useState<
    PendingCustomFieldDef[]
  >([]);
  const [isCreateCfDialogOpen, setIsCreateCfDialogOpen] = useState(false);
  const [editingCreateCfIndex, setEditingCreateCfIndex] = useState<
    number | null
  >(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const tree = useMemo(
    () =>
      foldersEnabled
        ? buildTree(folders, categories)
        : categories.map((c) => ({
            type: 'category' as const,
            id: `cat-${c.id}`,
            name: c.name,
            code: c.code ?? '',
            categoryId: c.id,
          })),
    [foldersEnabled, folders, categories],
  );

  function findFolderChildren(
    nodes: TreeNode[],
    folderId: string,
  ): TreeNode[] | null {
    for (const node of nodes) {
      if (node.type === 'folder' && node.id === folderId) {
        return node.children;
      }
      if (node.type === 'folder') {
        const found = findFolderChildren(node.children, folderId);
        if (found !== null) return found;
      }
    }
    return null;
  }

  const childrenOfSelectedFolder = useMemo((): TreeNode[] => {
    if (
      !selectedNode ||
      selectedNode.type !== 'folder' ||
      !foldersEnabled
    ) {
      return [];
    }
    return findFolderChildren(tree, selectedNode.id) ?? [];
  }, [selectedNode, foldersEnabled, tree]);

  const displayedItems = useMemo((): Item[] => {
    if (!selectedNode) return [];
    if (selectedNode.type === 'category') {
      return items.filter((i) => i.categoryId === selectedNode.id);
    }
    return [];
  }, [selectedNode, items]);

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    control: controlCreate,
    formState: { errors: createErrors, isSubmitting: isCreating },
    reset: resetCreate,
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: '',
      folderId: '',
      code: '',
    },
  });

  const {
    register: registerFolder,
    handleSubmit: handleSubmitFolder,
    control: controlFolder,
    formState: { errors: folderErrors, isSubmitting: isCreatingFolder },
    reset: resetFolder,
  } = useForm<z.infer<typeof folderFormSchema>>({
    resolver: zodResolver(folderFormSchema),
    defaultValues: {
      name: '',
      parentFolderId: '' as string | undefined,
      code: '',
    },
  });

  const {
    register: registerCreateCf,
    handleSubmit: handleSubmitCreateCf,
    reset: resetCreateCf,
    watch: watchCreateCf,
    setValue: setValueCreateCf,
    formState: { errors: createCfErrors, isSubmitting: isCreatingCf },
  } = useForm<z.infer<typeof createCfFormSchema>>({
    resolver: zodResolver(createCfFormSchema),
    defaultValues: {
      name: '',
      key: '',
      type: 'text',
      required: false,
      options: '',
    },
  });

  const watchCreateCfType = watchCreateCf('type');

  const loadData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const [categoriesData, itemsData, branchesData, shelvesData, foldersTree] =
        await Promise.all([
          listCategories(),
          listItems(),
          listBranches(),
          listShelves(),
          foldersEnabled ? getFoldersTree() : Promise.resolve([]),
        ]);
      setCategories(categoriesData);
      setItems(itemsData);
      setBranches(branchesData);
      setShelves(shelvesData);
      if (foldersEnabled) {
        setFolders(foldersTree);
      }
    } catch (err) {
      const ax = err as { response?: { status?: number; data?: { message?: string } } };
      const status = ax?.response?.status;
      const apiMessage = typeof ax?.response?.data?.message === 'string' ? ax.response.data.message : undefined;
      const errorMsg =
        status === 403
          ? t('pages.categories.error.permissionDenied')
          : apiMessage?.trim()
            ? apiMessage
            : err instanceof Error && err.message?.trim()
              ? err.message
              : errorLoad?.trim()
                ? errorLoad
                : t('pages.categories.error.load') || 'An error occurred while loading data.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [errorLoad, foldersEnabled, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const getItemCount = useCallback(
    (categoryId: string): number =>
      items.filter((i) => i.categoryId === categoryId).length,
    [items],
  );

  const handleCreate = async (values: CategoryFormValues): Promise<void> => {
    try {
      const payload: CreateCategoryPayload = {
        name: values.name,
        folderId: values.folderId || undefined,
        code: values.code?.trim() || undefined,
      };
      const newCategory = await createCategory(payload);
      for (const def of createPendingCustomFields) {
        await createCustomFieldDefinition({
          name: def.name,
          key: def.key,
          type: def.type,
          required: def.required ?? false,
          options: def.options,
          categoryId: newCategory.id,
        });
      }
      setIsCreateDialogOpen(false);
      resetCreate();
      setCreatePendingCustomFields([]);
      toast.success(successCreate);
      void loadData();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : errorCreate;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleAddCreateCf = (): void => {
    setEditingCreateCfIndex(null);
    resetCreateCf({
      name: '',
      key: '',
      type: 'text',
      required: false,
      options: '',
    });
    setIsCreateCfDialogOpen(true);
  };

  const handleEditCreateCf = (index: number): void => {
    const def = createPendingCustomFields[index];
    if (!def) return;
    setEditingCreateCfIndex(index);
    resetCreateCf({
      name: def.name,
      key: def.key,
      type: def.type,
      required: def.required,
      options: def.options?.join(', ') ?? '',
    });
    setIsCreateCfDialogOpen(true);
  };

  const handleSubmitCreateCfForm = (
    values: z.infer<typeof createCfFormSchema>,
  ): void => {
    const options = values.options
      ? values.options.split(',').map((s) => s.trim()).filter(Boolean)
      : undefined;
    const def: PendingCustomFieldDef = {
      name: values.name,
      key: values.key,
      type: values.type as CustomFieldType,
      required: values.required ?? false,
      options,
    };
    if (editingCreateCfIndex !== null) {
      setCreatePendingCustomFields((prev) => {
        const next = [...prev];
        next[editingCreateCfIndex] = def;
        return next;
      });
    } else {
      setCreatePendingCustomFields((prev) => [...prev, def]);
    }
    setIsCreateCfDialogOpen(false);
    setEditingCreateCfIndex(null);
    resetCreateCf();
  };

  const handleRemoveCreateCf = (index: number): void => {
    setCreatePendingCustomFields((prev) =>
      prev.filter((_, i) => i !== index),
    );
  };

  const createDialogTitle = useIndustryTranslation(
    'pages.categories.dialogs.create.title',
  );
  const createDialogDescription = useIndustryTranslation(
    'pages.categories.dialogs.create.description',
  );

  const handleCreateFolder = async (
    values: z.infer<typeof folderFormSchema>,
  ): Promise<void> => {
    try {
      const payload: CreateFolderPayload = {
        name: values.name,
        parentFolderId:
          values.parentFolderId && values.parentFolderId.trim()
            ? values.parentFolderId
            : undefined,
        code: values.code?.trim() || undefined,
      };
      await createFolder(payload);
      setIsFolderDialogOpen(false);
      resetFolder();
      toast.success(t('pages.folders.success.create'));
      void loadData();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('pages.folders.error.create'),
      );
    }
  };

  const handleEditFolder = async (
    values: z.infer<typeof folderFormSchema>,
  ): Promise<void> => {
    if (!editingFolder) return;
    try {
      await updateFolder(editingFolder.id, {
        name: values.name,
        parentFolderId:
          values.parentFolderId && values.parentFolderId.trim()
            ? values.parentFolderId
            : undefined,
        code: values.code?.trim() || undefined,
      });
      setEditingFolder(null);
      resetFolder();
      toast.success(t('pages.folders.success.update'));
      void loadData();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('pages.folders.error.update'),
      );
    }
  };

  const handleDeleteFolder = async (cascade: boolean): Promise<void> => {
    if (!deletingFolder) return;
    setDeletingFolderAction(cascade ? 'delete' : 'move');
    try {
      const payload = cascade
        ? undefined
        : { moveToFolderId: deleteMoveToFolderId || undefined };
      await deleteFolder(deletingFolder.id, payload);
      setDeletingFolder(null);
      setDeleteMoveToFolderId('');
      toast.success(t('pages.folders.success.delete'));
      void loadData();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('pages.folders.error.delete'),
      );
    } finally {
      setDeletingFolderAction(null);
    }
  };

  const handleMoveConfirm = async (): Promise<void> => {
    if (!movingNode) return;
    try {
      if (movingNode.type === 'category') {
        await updateCategory(movingNode.id, {
          folderId: moveDestinationFolderId || null,
        });
        toast.success(t('pages.categories.success.update'));
      } else {
        await updateFolder(movingNode.id, {
          parentFolderId: moveDestinationFolderId || null,
        });
        toast.success(t('pages.folders.success.update'));
      }
      setMovingNode(null);
      setMoveDestinationFolderId('');
      void loadData();
    } catch (err) {
      const msg =
        movingNode.type === 'category'
          ? t('pages.categories.error.update')
          : t('pages.folders.error.update');
      toast.error(err instanceof Error ? err.message : msg);
    }
  };

  const handleDragStart = useCallback((event: DragStartEvent): void => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent): Promise<void> => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over) return;
      const activeId = String(active.id);
      const overId = String(over.id);

      const isProduct = activeId.startsWith(DRAG_ITEM_PREFIX);
      if (isProduct) {
        const itemId = activeId.replace(DRAG_ITEM_PREFIX, '');
        const item = items.find((i) => i.id === itemId);
        if (!item) return;
        if (overId.startsWith(DROP_CAT_PREFIX)) {
          const categoryId = overId.replace(DROP_CAT_PREFIX, '');
          if (item.categoryId === categoryId) return;
          try {
            await updateItem(itemId, { categoryId });
            toast.success(t('pages.items.success.update'));
            void loadData();
          } catch (err) {
            toast.error(
              err instanceof Error ? err.message : t('pages.items.error.update'),
            );
          }
        } else if (overId.startsWith(DROP_FOLDER_PREFIX)) {
          const folderId = overId.replace(DROP_FOLDER_PREFIX, '');
          const folderCategories = categories.filter(
            (c) => c.folderId === folderId,
          );
          const flat = flattenFolders(folders);
          const folder = flat.find((f) => f.id === folderId);
          const folderName = folder?.name ?? folderId;
          if (folderCategories.length === 0) {
            toast.error(t('pages.items.dialogs.move.noCategoriesInFolder'));
          } else if (folderCategories.length === 1) {
            const categoryId = folderCategories[0]!.id;
            if (item.categoryId === categoryId) return;
            try {
              await updateItem(itemId, { categoryId });
              toast.success(t('pages.items.success.update'));
              void loadData();
            } catch (err) {
              toast.error(
                err instanceof Error
                  ? err.message
                  : t('pages.items.error.update'),
              );
            }
          } else {
            setProductMovePending({
              itemId,
              folderId,
              folderName,
            });
          }
        }
        return;
      }

      const isCategory =
        activeId.startsWith(DRAG_CAT_PREFIX) ||
        activeId.startsWith(DRAG_RIGHT_CAT_PREFIX);
      const isFolder =
        activeId.startsWith(DRAG_FOLDER_PREFIX) ||
        activeId.startsWith(DRAG_RIGHT_FOLDER_PREFIX);
      if (!isCategory && !isFolder) return;
      const itemId = isCategory
        ? activeId.replace(DRAG_RIGHT_CAT_PREFIX, '').replace(DRAG_CAT_PREFIX, '')
        : activeId
            .replace(DRAG_RIGHT_FOLDER_PREFIX, '')
            .replace(DRAG_FOLDER_PREFIX, '');
      let destinationId = '';
      let destinationName = t('pages.folders.form.all');
      if (overId === DROP_ROOT_ID) {
        destinationId = '';
      } else if (overId.startsWith(DROP_FOLDER_PREFIX)) {
        destinationId = overId.replace(DROP_FOLDER_PREFIX, '');
        const flat = flattenFolders(folders);
        const destFolder = flat.find((f) => f.id === destinationId);
        destinationName = destFolder?.name ?? destinationId;
      } else if (overId.startsWith(DROP_CAT_PREFIX)) {
        return;
      } else {
        return;
      }
      if (isFolder && itemId === destinationId) return;
      if (isFolder && destinationId) {
        const flat = flattenFolders(folders);
        const movingFolder = flat.find((f) => f.id === itemId);
        if (movingFolder) {
          const descendantIds = flattenFolders(movingFolder.children || []).map(
            (c) => c.id,
          );
          if (descendantIds.includes(destinationId)) return;
        }
      }
      const itemName =
        isCategory
          ? categories.find((c) => c.id === itemId)?.name ?? itemId
          : flattenFolders(folders).find((f) => f.id === itemId)?.name ?? itemId;
      setDndPendingMove({
        item: {
          type: isCategory ? 'category' : 'folder',
          id: itemId,
          name: itemName,
        },
        destinationId,
        destinationName,
      });
    },
    [folders, categories, items, loadData, t],
  );

  const handleItemMoveConfirm = useCallback(async (): Promise<void> => {
    if (!movingItem || !moveDestinationCategoryId) return;
    try {
      await updateItem(movingItem.id, {
        categoryId: moveDestinationCategoryId,
      });
      setMovingItem(null);
      setMoveDestinationCategoryId('');
      toast.success(t('pages.items.success.update'));
      void loadData();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('pages.items.error.update'),
      );
    }
  }, [movingItem, moveDestinationCategoryId, loadData, t]);

  const handleProductMovePendingConfirm = useCallback(
    async (categoryId: string): Promise<void> => {
      if (!productMovePending) return;
      try {
        await updateItem(productMovePending.itemId, { categoryId });
        setProductMovePending(null);
        toast.success(t('pages.items.success.update'));
        void loadData();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : t('pages.items.error.update'),
        );
      }
    },
    [productMovePending, loadData, t],
  );

  const handleDndConfirm = useCallback(async (): Promise<void> => {
    if (!dndPendingMove) return;
    const { item, destinationId } = dndPendingMove;
    setIsDndConfirming(true);
    try {
      if (item.type === 'category') {
        await updateCategory(item.id, { folderId: destinationId || null });
        toast.success(t('pages.categories.success.update'));
      } else {
        await updateFolder(item.id, {
          parentFolderId: destinationId || null,
        });
        toast.success(t('pages.folders.success.update'));
      }
      setDndPendingMove(null);
      void loadData();
    } catch (err) {
      const msg =
        item.type === 'category'
          ? t('pages.categories.error.update')
          : t('pages.folders.error.update');
      toast.error(err instanceof Error ? err.message : msg);
    } finally {
      setIsDndConfirming(false);
    }
  }, [dndPendingMove, loadData, t]);

  const toggleFolderExpand = (folderId: string): void => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const TREE_INDENT = 20;

  const DRAG_CAT_PREFIX = 'drag-cat-';
  const DRAG_FOLDER_PREFIX = 'drag-folder-';
  const DRAG_RIGHT_CAT_PREFIX = 'drag-right-cat-';
  const DRAG_RIGHT_FOLDER_PREFIX = 'drag-right-folder-';
  const DRAG_ITEM_PREFIX = 'drag-item-';
  const DROP_FOLDER_PREFIX = 'drop-folder-';
  const DROP_CAT_PREFIX = 'drop-cat-';
  const DROP_ROOT_ID = 'drop-root';

  function SimpleCategoryRow({
    node,
    depth,
  }: {
    node: Extract<TreeNode, { type: 'category' }>;
    depth: number;
  }): JSX.Element {
    const count = getItemCount(node.categoryId);
    const isSelected =
      selectedNode?.type === 'category' && selectedNode.id === node.categoryId;
    const { setNodeRef, isOver } = useDroppable({
      id: `${DROP_CAT_PREFIX}${node.categoryId}`,
    });
    return (
      <div key={node.id} className="flex min-w-0 items-stretch">
        <TreeIndent depth={depth} />
        <div
          ref={setNodeRef}
          role="button"
          tabIndex={0}
          className={
            'flex flex-1 min-w-0 items-center gap-2 py-1.5 pl-1 pr-2 rounded-md cursor-pointer text-sm hover:bg-muted hover:bg-opacity-50 ' +
            (isSelected ? 'bg-muted' : '') +
            (isOver ? ' ring-2 ring-primary ring-opacity-50' : '')
          }
          onClick={() => selectNode({ type: 'category', id: node.categoryId })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                selectNode({ type: 'category', id: node.categoryId });
              }
            }}
        >
          <div className="w-6 shrink-0 flex items-center justify-center">
            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
          </div>
          <span className="text-muted-foreground text-xs shrink-0 tabular-nums">{node.code}</span>
          <span className="truncate flex-1 min-w-0">{node.name}</span>
          <Badge variant="secondary" className="text-xs shrink-0">
            {count}
          </Badge>
          {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/categories/${node.categoryId}/edit`);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                {t('pages.categories.actions.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  setCategoryToDelete({ id: node.categoryId, name: node.name });
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('pages.categories.actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>
    );
  }

  function DraggableCategoryRow({
    node,
    depth,
  }: {
    node: Extract<TreeNode, { type: 'category' }>;
    depth: number;
  }): JSX.Element {
    const count = getItemCount(node.categoryId);
    const isSelected =
      selectedNode?.type === 'category' && selectedNode.id === node.categoryId;
    const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
      id: `${DRAG_CAT_PREFIX}${node.categoryId}`,
    });
    const { setNodeRef: setDropRef, isOver } = useDroppable({
      id: `${DROP_CAT_PREFIX}${node.categoryId}`,
    });
    const setRef = useCallback(
      (el: HTMLDivElement | null) => {
        setDragRef(el);
        setDropRef(el);
      },
      [setDragRef, setDropRef],
    );
    return (
      <div key={node.id} className="flex min-w-0 items-stretch">
        <TreeIndent depth={depth} />
        <div
          ref={setRef}
          {...attributes}
          {...listeners}
          role="button"
          tabIndex={0}
          className={
            'flex flex-1 min-w-0 items-center gap-2 py-1.5 pl-1 pr-2 rounded-md cursor-grab active:cursor-grabbing text-sm hover:bg-muted hover:bg-opacity-50 ' +
            (isSelected ? 'bg-muted' : '') +
            (isOver ? ' ring-2 ring-primary ring-opacity-50' : '')
          }
          onClick={(e) => {
            e.stopPropagation();
            selectNode({ type: 'category', id: node.categoryId });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectNode({ type: 'category', id: node.categoryId });
            }
          }}
        >
          <div className="w-6 shrink-0 flex items-center justify-center">
            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
          </div>
          <span className="text-muted-foreground text-xs shrink-0 tabular-nums">{node.code}</span>
          <span className="truncate flex-1 min-w-0">{node.name}</span>
          <Badge variant="secondary" className="text-xs shrink-0">
            {count}
          </Badge>
          {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/categories/${node.categoryId}/edit`);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                {t('pages.categories.actions.edit')}
              </DropdownMenuItem>
              {foldersEnabled && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setMovingNode({
                      type: 'category',
                      id: node.categoryId,
                      name: node.name,
                    });
                    setMoveDestinationFolderId('');
                  }}
                >
                  <Move className="mr-2 h-4 w-4" />
                  {t('pages.categories.actions.move')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  setCategoryToDelete({ id: node.categoryId, name: node.name });
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('pages.categories.actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>
    );
  }

  function DraggableFolderRow({
    node,
    depth,
    renderChildren,
  }: {
    node: Extract<TreeNode, { type: 'folder' }>;
    depth: number;
    renderChildren: () => JSX.Element[];
  }): JSX.Element {
    const isExpanded = expandedFolderIds.has(node.id);
    const isSelected =
      selectedNode?.type === 'folder' && selectedNode.id === node.id;
    const hasChildren = node.children.length > 0;
    const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
      id: `${DRAG_FOLDER_PREFIX}${node.id}`,
    });
    const { setNodeRef: setDropRef, isOver } = useDroppable({
      id: `${DROP_FOLDER_PREFIX}${node.id}`,
    });
    const setRef = useCallback(
      (el: HTMLDivElement | null) => {
        setDragRef(el);
        setDropRef(el);
      },
      [setDragRef, setDropRef],
    );
    return (
      <Collapsible
        key={node.id}
        open={isExpanded}
        onOpenChange={() => toggleFolderExpand(node.id)}
      >
        <div className="flex min-w-0 items-stretch">
          <TreeIndent depth={depth} />
          <div
            ref={setRef}
            {...attributes}
            {...listeners}
            className={
              'flex flex-1 min-w-0 items-center gap-1 py-1.5 pl-1 pr-2 rounded-md cursor-grab active:cursor-grabbing text-sm group hover:bg-muted hover:bg-opacity-50 ' +
              (isSelected ? 'bg-muted' : '') +
              (isOver ? ' ring-2 ring-primary ring-opacity-50' : '')
            }
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('button')) return;
              selectNode({ type: 'folder', id: node.id });
            }}
          >
            <CollapsibleTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 p-0">
                {hasChildren && isExpanded
                  ? <ChevronDown className="h-4 w-4"></ChevronDown>
                  : <ChevronRight className="h-4 w-4"></ChevronRight>}
              </Button>
            </CollapsibleTrigger>
            {isExpanded
              ? <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground"></FolderOpen>
              : <Folder className="h-4 w-4 shrink-0 text-muted-foreground"></Folder>}
            <span className="text-muted-foreground text-xs shrink-0 tabular-nums">{node.code}</span>
            <span className="truncate flex-1 min-w-0">{node.name}</span>
            {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingFolder(
                      flattenFolders(folders).find((f) => f.id === node.id) ?? null,
                    );
                    resetFolder({
                      name: node.name,
                      parentFolderId:
                        flattenFolders(folders).find((f) => f.id === node.id)
                          ?.parentFolderId ?? '',
                      code: node.code ?? '',
                    });
                    setIsFolderDialogOpen(true);
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('pages.folders.actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setMovingNode({
                      type: 'folder',
                      id: node.id,
                      name: node.name,
                    });
                    setMoveDestinationFolderId('');
                  }}
                >
                  <Move className="mr-2 h-4 w-4" />
                  {t('pages.folders.actions.move')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    const f = flattenFolders(folders).find((x) => x.id === node.id);
                    if (f) {
                      setDeletingFolder(f);
                      setDeleteMoveToFolderId('');
                    }
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('pages.folders.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </div>
        <CollapsibleContent>
          {renderChildren()}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  function RootDropZone(): JSX.Element {
    const { setNodeRef, isOver } = useDroppable({ id: DROP_ROOT_ID });
    return (
      <div
        ref={setNodeRef}
        className={
          'mb-1 min-h-[28px] rounded-md border border-dashed border-muted-foreground border-opacity-30 flex items-center px-2 text-xs text-muted-foreground ' +
          (isOver ? 'bg-primary bg-opacity-10 border-primary' : '')
        }
      >
        {t('pages.folders.form.all')}
      </div>
    );
  }

  function DraggableProductRow({
    item,
    branch,
    shelf,
    statusDetectedLabel,
    statusNotSeenRecentlyLabel,
    statusMissingLabel,
  }: {
    item: Item;
    branch: Branch | undefined;
    shelf: Shelf | undefined;
    statusDetectedLabel: string;
    statusNotSeenRecentlyLabel: string;
    statusMissingLabel: string;
  }): JSX.Element {
    const { attributes, listeners, setNodeRef } = useDraggable({
      id: `${DRAG_ITEM_PREFIX}${item.id}`,
    });
    const detectionStatus = getItemDetectionStatus(item);
    const statusLabel =
      detectionStatus === 'detected'
        ? statusDetectedLabel
        : detectionStatus === 'not_seen_recently'
          ? statusNotSeenRecentlyLabel
          : statusMissingLabel;
    return (
      <TableRow
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        onRowClick={() => navigate(`/items/${item.id}`)}
        className="cursor-grab active:cursor-grabbing"
      >
        <TableCell className="font-medium">{item.name}</TableCell>
        <TableCell className="text-muted-foreground font-mono text-sm">
          {item.sku}
        </TableCell>
        <TableCell className="text-muted-foreground">
          {branch?.name ?? '—'}
        </TableCell>
        <TableCell className="text-muted-foreground">
          {shelf?.name ?? '—'}
        </TableCell>
        <TableCell>
          <ProductStatusBadge status={detectionStatus} label={statusLabel} />
        </TableCell>
        <TableCell>
          {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/items/${item.id}`);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                {t('pages.items.details.actions.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setMovingItem({ id: item.id, name: item.name });
                  setMoveDestinationCategoryId('');
                }}
              >
                <Move className="mr-2 h-4 w-4" />
                {t('pages.categories.actions.move')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </TableCell>
      </TableRow>
    );
  }

  function FoldersListChildRow({
    child,
  }: {
    child: TreeNode;
  }): JSX.Element {
    const isFolder = child.type === 'folder';
    const { attributes, listeners, setNodeRef } = useDraggable({
      id: isFolder
        ? `${DRAG_RIGHT_FOLDER_PREFIX}${child.id}`
        : `${DRAG_RIGHT_CAT_PREFIX}${child.categoryId}`,
    });
    return (
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset cursor-grab active:cursor-grabbing"
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('button')) return;
          selectNode(
            isFolder
              ? { type: 'folder', id: child.id }
              : { type: 'category', id: child.categoryId },
          );
        }}
      >
        {isFolder ? (
          <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <LayoutGrid className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate flex-1 font-medium">{child.name}</span>
        {child.type === 'category' && (
          <Badge variant="secondary" className="text-xs shrink-0">
            {getItemCount(child.categoryId)}
          </Badge>
        )}
        {isAdmin && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (isFolder) {
                  setEditingFolder(
                    flattenFolders(folders).find((f) => f.id === child.id) ??
                      null,
                  );
                  resetFolder({
                    name: child.name,
                    parentFolderId:
                      flattenFolders(folders).find((f) => f.id === child.id)
                        ?.parentFolderId ?? '',
                    code: child.code ?? '',
                  });
                  setIsFolderDialogOpen(true);
                } else {
                  navigate(`/categories/${child.categoryId}/edit`);
                }
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t('pages.categories.actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setMovingNode({
                  type: isFolder ? 'folder' : 'category',
                  id: isFolder ? child.id : child.categoryId,
                  name: child.name,
                });
                setMoveDestinationFolderId('');
              }}
            >
              <Move className="mr-2 h-4 w-4" />
              {t('pages.categories.actions.move')}
            </DropdownMenuItem>
            {isFolder ? (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  const f = flattenFolders(folders).find((x) => x.id === child.id);
                  if (f) {
                    setDeletingFolder(f);
                    setDeleteMoveToFolderId('');
                  }
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('pages.folders.actions.delete')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  setCategoryToDelete({ id: child.categoryId, name: child.name });
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('pages.categories.actions.delete')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    );
  }

  const TreeIndent = ({ depth }: { depth: number }): JSX.Element => {
    if (depth <= 0) return <></>;
    return (
      <div
        className="flex shrink-0"
        style={{ width: depth * TREE_INDENT }}
        aria-hidden
      >
        {Array.from({ length: depth }).map((_, i) => (
          <div
            key={i}
            className="shrink-0 border-l-2 border-muted-foreground border-opacity-30 self-stretch min-h-[2rem]"
            style={{ width: TREE_INDENT }}
          />
        ))}
      </div>
    );
  };

  const renderTreeNode = (node: TreeNode, depth: number): JSX.Element => {
    if (node.type === 'category') {
      return foldersEnabled ? (
        <DraggableCategoryRow key={node.id} node={node} depth={depth} />
      ) : (
        <SimpleCategoryRow key={node.id} node={node} depth={depth} />
      );
    }
    return (
      <DraggableFolderRow
        key={node.id}
        node={node}
        depth={depth}
        renderChildren={() =>
          node.children.map((child) => renderTreeNode(child, depth + 1))
        }
      />
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] gap-6 p-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          isAdmin ? (
          <div className="flex gap-2">
            {foldersEnabled && (
              <Button
                variant="outline"
                onClick={async () => {
                  setEditingFolder(null);
                  const parentFolderId =
                    selectedNode?.type === 'folder' ? selectedNode.id : '';
                  let suggestedCode = '';
                  try {
                    const next = await getNextFolderCode();
                    suggestedCode = next.code ?? '';
                  } catch {
                    // leave empty; backend will generate
                  }
                  resetFolder({
                    name: '',
                    parentFolderId,
                    code: suggestedCode,
                  });
                  setIsFolderDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('pages.folders.actions.add')}
              </Button>
            )}
            <Button
              onClick={async () => {
                const folderId =
                  selectedNode?.type === 'folder'
                    ? selectedNode.id
                    : selectedNode?.type === 'category'
                      ? categories.find((c) => c.id === selectedNode.id)
                          ?.folderId ?? ''
                      : '';
                let suggestedCode = '';
                try {
                  const next = await getNextCategoryCode();
                  suggestedCode = next.code ?? '';
                } catch {
                  // leave empty; backend will generate
                }
                resetCreate({
                  name: '',
                  folderId: folderId || '',
                  code: suggestedCode,
                });
                setIsCreateDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              {createLabel}
            </Button>
          </div>
          ) : undefined
        }
      />

      {error?.trim() ? (
        <Card className="border-destructive bg-destructive/10 mb-4">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-foreground mb-1">
              {t('pages.categories.error.load')}
            </p>
            <p className="text-sm text-muted-foreground">
              {error.trim()}
            </p>
          </CardContent>
        </Card>
      ) : null}

      <div className="flex flex-1 min-h-0 gap-4">
        {tree.length > 0 && !isLoading ? (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={(e) => void handleDragEnd(e)}
            onDragCancel={() => setActiveDragId(null)}
          >
            <div
              className="flex flex-col w-72 min-w-[280px] shrink-0 border rounded-lg bg-card"
              style={{ minHeight: 300 }}
            >
              <div className="p-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-sm">
                  {foldersEnabled
                    ? t('pages.categories.tree.title')
                    : t('pages.categories.tree.categoriesOnly')}
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                <div className="space-y-0.5">
                  {foldersEnabled && <RootDropZone />}
                  {tree.map((node) => renderTreeNode(node, 0))}
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0 flex flex-col border rounded-lg bg-card">
          <div className="p-3 border-b flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-semibold text-sm flex items-center gap-1 flex-wrap min-w-0">
              {selectedNode ? (
                getBreadcrumbPath(
                  selectedNode,
                  folders,
                  categories,
                  t('pages.folders.form.all'),
                ).map((segment, i, arr) => {
                  const isLast = i === arr.length - 1;
                  const handleClick = () => {
                    if (segment.type === 'root') {
                      selectNode(null);
                    } else if (segment.type === 'folder') {
                      selectNode({ type: 'folder', id: segment.id });
                    } else {
                      selectNode({ type: 'category', id: segment.id });
                    }
                  };
                  return (
                    <span key={i} className="flex items-center gap-1">
                      {i > 0 && (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      )}
                      {isLast ? (
                        <span className="truncate font-medium">
                          {segment.name}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleClick}
                          className="truncate text-left hover:underline hover:text-foreground text-muted-foreground focus:outline-none focus:underline"
                        >
                          {segment.name}
                        </button>
                      )}
                    </span>
                  );
                })
              ) : (
                t('pages.categories.items.selectPrompt')
              )}
            </h3>
            {selectedNode && isAdmin && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => {
                  if (selectedNode.type === 'folder') {
                    const folder = flattenFolders(folders).find(
                      (f) => f.id === selectedNode.id,
                    );
                    if (folder) {
                      setEditingFolder(folder);
                      resetFolder({
                        name: folder.name,
                        parentFolderId: folder.parentFolderId ?? '',
                        code: folder.code ?? '',
                      });
                      setIsFolderDialogOpen(true);
                    }
                  } else {
                    navigate(`/categories/${selectedNode.id}/edit`);
                  }
                }}
              >
                <Pencil className="h-4 w-4 mr-1.5" />
                {selectedNode.type === 'folder'
                  ? t('pages.folders.actions.edit')
                  : t('pages.categories.actions.edit')}
              </Button>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {!selectedNode ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('pages.categories.items.selectPrompt')}
                </p>
              </div>
            ) : selectedNode.type === 'folder' && foldersEnabled ? (
              childrenOfSelectedFolder.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t('pages.categories.items.folderEmpty')}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {childrenOfSelectedFolder.map((child) => (
                    <FoldersListChildRow key={child.id} child={child} />
                  ))}
                </div>
              )
            ) : displayedItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('pages.categories.items.empty')}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{nameTableLabel}</TableHead>
                    <TableHead>{skuTableLabel}</TableHead>
                    <TableHead>{branchTableLabel}</TableHead>
                    <TableHead>{shelfTableLabel}</TableHead>
                    <TableHead>{statusTableLabel}</TableHead>
                    <TableHead className="w-[70px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedItems.map((item) => {
                    const branch = branches.find((b) => b.id === item.branchId);
                    const shelf = shelves.find(
                      (s) => s.id === item.currentShelfId,
                    );
                    return (
                      <DraggableProductRow
                        key={item.id}
                        item={item}
                        branch={branch}
                        shelf={shelf}
                        statusDetectedLabel={statusDetectedLabel}
                        statusNotSeenRecentlyLabel={statusNotSeenRecentlyLabel}
                        statusMissingLabel={statusMissingLabel}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

            <DragOverlay dropAnimation={null}>
              {activeDragId ? (
                activeDragId.startsWith(DRAG_ITEM_PREFIX) ? (
                  (() => {
                    const itemId = activeDragId.replace(DRAG_ITEM_PREFIX, '');
                    const item = items.find((i) => i.id === itemId);
                    return item ? (
                      <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 shadow-lg">
                        <span className="truncate text-sm font-medium">
                          {item.name}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {item.sku}
                        </span>
                      </div>
                    ) : null;
                  })()
                ) : (
                  (() => {
                    const isCategory =
                      activeDragId.startsWith(DRAG_CAT_PREFIX) ||
                      activeDragId.startsWith(DRAG_RIGHT_CAT_PREFIX);
                    const id = isCategory
                      ? activeDragId
                          .replace(DRAG_RIGHT_CAT_PREFIX, '')
                          .replace(DRAG_CAT_PREFIX, '')
                      : activeDragId
                          .replace(DRAG_RIGHT_FOLDER_PREFIX, '')
                          .replace(DRAG_FOLDER_PREFIX, '');
                    const name = isCategory
                      ? categories.find((c) => c.id === id)?.name ?? id
                      : flattenFolders(folders).find((f) => f.id === id)
                          ?.name ?? id;
                    const count = isCategory
                      ? getItemCount(id)
                      : undefined;
                    return (
                      <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 shadow-lg">
                        {isCategory ? (
                          <>
                            <LayoutGrid className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="truncate text-sm font-medium">
                              {name}
                            </span>
                            {count !== undefined && (
                              <Badge variant="secondary" className="text-xs">
                                {count}
                              </Badge>
                            )}
                          </>
                        ) : (
                          <>
                            <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="truncate text-sm font-medium">
                              {name}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })()
                )
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <>
            <div
              className="flex flex-col w-72 min-w-[280px] shrink-0 border rounded-lg bg-card"
              style={{ minHeight: 300 }}
            >
              <div className="p-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-sm">
                  {foldersEnabled
                    ? t('pages.categories.tree.title')
                    : t('pages.categories.tree.categoriesOnly')}
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {isLoading ? (
                  <div className="flex flex-col items-center gap-3 py-8">
                    <LoadingLogo size="sm" />
                    <span className="text-sm text-muted-foreground">
                      {loadingLabel}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 px-2">
                    {emptyLabel}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0 flex flex-col border rounded-lg bg-card">
              <div className="p-3 border-b">
                <h3 className="font-semibold text-sm flex items-center gap-1 flex-wrap">
                  {selectedNode
                    ? getBreadcrumbPath(
                        selectedNode,
                        folders,
                        categories,
                        t('pages.folders.form.all'),
                      ).map((segment, i, arr) => {
                        const isLast = i === arr.length - 1;
                        const handleClick = () => {
                          if (segment.type === 'root') {
                            selectNode(null);
                          } else if (segment.type === 'folder') {
                            selectNode({ type: 'folder', id: segment.id });
                          } else {
                            selectNode({ type: 'category', id: segment.id });
                          }
                        };
                        return (
                          <span key={i} className="flex items-center gap-1">
                            {i > 0 && (
                              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            )}
                            {isLast ? (
                              <span className="truncate font-medium">
                                {segment.name}
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={handleClick}
                                className="truncate text-left hover:underline hover:text-foreground text-muted-foreground focus:outline-none focus:underline"
                              >
                                {segment.name}
                              </button>
                            )}
                          </span>
                        );
                      })
                    : t('pages.categories.items.selectPrompt')}
                </h3>
              </div>
              <div className="flex-1 overflow-auto">
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t('pages.categories.items.selectPrompt')}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) setCreatePendingCustomFields([]);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createDialogTitle}</DialogTitle>
            <DialogDescription>{createDialogDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCreate(handleCreate)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">
                  {t('pages.categories.form.name')} *
                </Label>
                <Input
                  id="create-name"
                  {...registerCreate('name')}
                  placeholder={t('pages.categories.form.name')}
                />
                {createErrors.name && (
                  <p className="text-sm text-destructive">
                    {t(
                      createErrors.name.message ||
                        'pages.categories.form.nameRequired',
                    )}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-code">
                  {t('pages.categories.form.code')}
                </Label>
                <Input
                  id="create-code"
                  {...registerCreate('code')}
                  placeholder={t('pages.categories.form.codePlaceholder')}
                  maxLength={3}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  {t('pages.categories.form.codeDescription')}
                </p>
                {createErrors.code && (
                  <p className="text-sm text-destructive">
                    {t(
                      createErrors.code.message ||
                        'pages.categories.form.codeInvalid',
                    )}
                  </p>
                )}
              </div>
              {foldersEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="create-folder">
                    {t('pages.categories.form.folder')}
                  </Label>
                  <Controller
                    name="folderId"
                    control={controlCreate}
                    render={({ field }) => (
                      <Select
                        value={field.value || 'none'}
                        onValueChange={(v) =>
                          field.onChange(v === 'none' ? '' : v)
                        }
                      >
                        <SelectTrigger id="create-folder">
                          <SelectValue
                            placeholder={t('pages.folders.form.all')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            {t('pages.folders.form.all')}
                          </SelectItem>
                          {flattenFolders(folders).map((n) => (
                            <SelectItem key={n.id} value={n.id}>
                              {'—'.repeat(n.level)} {n.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              )}
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base">
                    {t('pages.customFields.title')}
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddCreateCf}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('pages.customFields.actions.add')}
                  </Button>
                </div>
                {createPendingCustomFields.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('pages.customFields.empty')}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('pages.customFields.table.name')}</TableHead>
                        <TableHead>{t('pages.customFields.table.key')}</TableHead>
                        <TableHead>{t('pages.customFields.table.type')}</TableHead>
                        <TableHead>{t('pages.customFields.table.required')}</TableHead>
                        <TableHead className="w-[80px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {createPendingCustomFields.map((def, idx) => (
                        <TableRow key={`${def.key}-${idx}`}>
                          <TableCell className="font-medium">{def.name}</TableCell>
                          <TableCell className="font-mono text-sm">{def.key}</TableCell>
                          <TableCell>
                            {t(`pages.customFields.types.${def.type}`)}
                          </TableCell>
                          <TableCell>
                            {def.required
                              ? t('pages.common.yes')
                              : t('pages.common.no')}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditCreateCf(idx)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => handleRemoveCreateCf(idx)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetCreate();
                }}
              >
                {t('pages.categories.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating
                  ? t('pages.categories.actions.saving')
                  : createLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCreateCfDialogOpen}
        onOpenChange={(open) => {
          setIsCreateCfDialogOpen(open);
          if (!open) setEditingCreateCfIndex(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCreateCfIndex !== null
                ? t('pages.customFields.dialogs.edit.title')
                : t('pages.customFields.dialogs.create.title')}
            </DialogTitle>
            <DialogDescription>
              {editingCreateCfIndex !== null
                ? t('pages.customFields.dialogs.edit.description')
                : t('pages.customFields.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCreateCf(handleSubmitCreateCfForm)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-cf-name">
                  {t('pages.customFields.form.name')} *
                </Label>
                <Input
                  id="create-cf-name"
                  {...registerCreateCf('name', {
                    onChange: (e) => {
                      if (editingCreateCfIndex === null) {
                        const key = e.target.value
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, '_');
                        setValueCreateCf('key', key);
                      }
                    },
                  })}
                  placeholder={t('pages.customFields.form.namePlaceholder')}
                />
                {createCfErrors.name && (
                  <p className="text-sm text-destructive">
                    {t(
                      createCfErrors.name.message ??
                        'pages.customFields.form.nameRequired',
                    )}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-cf-key">
                  {t('pages.customFields.form.key')} *
                </Label>
                <Input
                  id="create-cf-key"
                  {...registerCreateCf('key')}
                  placeholder={t('pages.customFields.form.keyPlaceholder')}
                  disabled={editingCreateCfIndex !== null}
                />
                {createCfErrors.key && (
                  <p className="text-sm text-destructive">
                    {t(
                      createCfErrors.key.message ??
                        'pages.customFields.form.keyRequired',
                    )}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-cf-type">
                  {t('pages.customFields.form.type')} *
                </Label>
                <select
                  id="create-cf-type"
                  {...registerCreateCf('type')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="text">
                    {t('pages.customFields.types.text')}
                  </option>
                  <option value="textarea">
                    {t('pages.customFields.types.textarea')}
                  </option>
                  <option value="number">
                    {t('pages.customFields.types.number')}
                  </option>
                  <option value="url">
                    {t('pages.customFields.types.url')}
                  </option>
                  <option value="options">
                    {t('pages.customFields.types.options')}
                  </option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="create-cf-required"
                  checked={watchCreateCf('required')}
                  onCheckedChange={(checked) =>
                    setValueCreateCf('required', checked === true)
                  }
                />
                <Label htmlFor="create-cf-required" className="cursor-pointer">
                  {t('pages.customFields.form.required')}
                </Label>
              </div>
              {watchCreateCfType === 'options' && (
                <div className="space-y-2">
                  <Label htmlFor="create-cf-options">
                    {t('pages.customFields.form.options')}
                  </Label>
                  <Input
                    id="create-cf-options"
                    {...registerCreateCf('options')}
                    placeholder={t(
                      'pages.customFields.form.optionsPlaceholder',
                    )}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateCfDialogOpen(false);
                  setEditingCreateCfIndex(null);
                }}
              >
                {t('pages.categories.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCreatingCf}>
                {isCreatingCf
                  ? t('pages.categories.actions.saving')
                  : editingCreateCfIndex !== null
                    ? t('pages.customFields.actions.save')
                    : t('pages.customFields.actions.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {foldersEnabled && (
        <Dialog
          open={isFolderDialogOpen}
          onOpenChange={(open) => {
            setIsFolderDialogOpen(open);
            if (!open) setEditingFolder(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingFolder
                  ? t('pages.folders.dialogs.edit.title')
                  : t('pages.folders.dialogs.create.title')}
              </DialogTitle>
              <DialogDescription>
                {editingFolder
                  ? t('pages.folders.dialogs.edit.description')
                  : t('pages.folders.dialogs.create.description')}
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={handleSubmitFolder(
                editingFolder ? handleEditFolder : handleCreateFolder,
              )}
            >
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="folder-name">
                    {t('pages.folders.form.name')} *
                  </Label>
                  <Input
                    id="folder-name"
                    {...registerFolder('name')}
                    placeholder={t('pages.folders.form.namePlaceholder')}
                  />
                  {folderErrors.name && (
                    <p className="text-sm text-destructive">
                      {t(
                        folderErrors.name.message ||
                          'pages.folders.form.nameRequired',
                      )}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="folder-code">
                    {t('pages.folders.form.code')}
                  </Label>
                  <Input
                    id="folder-code"
                    {...registerFolder('code')}
                    placeholder={t('pages.folders.form.codePlaceholder')}
                    maxLength={3}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('pages.folders.form.codeDescription')}
                  </p>
                  {folderErrors.code && (
                    <p className="text-sm text-destructive">
                      {t(
                        folderErrors.code.message ||
                          'pages.folders.form.codeInvalid',
                      )}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="folder-parent">
                    {t('pages.folders.form.parentFolder')}
                  </Label>
                  <Controller
                    name="parentFolderId"
                    control={controlFolder}
                    render={({ field }) => {
                      const flat = flattenFolders(folders);
                      const excludeIds = editingFolder
                        ? [
                            editingFolder.id,
                            ...flattenFolders(
                              editingFolder.children || [],
                            ).map((c) => c.id),
                          ]
                        : [];
                      const options = flat.filter(
                        (n) => !excludeIds.includes(n.id),
                      );
                      return (
                        <Select
                          value={field.value || 'none'}
                          onValueChange={(v) =>
                            field.onChange(v === 'none' ? '' : v)
                          }
                        >
                          <SelectTrigger id="folder-parent">
                            <SelectValue
                              placeholder={t('pages.folders.form.all')}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              {t('pages.folders.form.all')}
                            </SelectItem>
                            {options.map((n) => (
                              <SelectItem key={n.id} value={n.id}>
                                {'—'.repeat(n.level)} {n.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsFolderDialogOpen(false);
                    resetFolder();
                  }}
                >
                  {t('pages.categories.actions.cancel')}
                </Button>
                <Button type="submit" disabled={isCreatingFolder}>
                  {isCreatingFolder
                    ? t('pages.categories.actions.saving')
                    : editingFolder
                      ? t('pages.folders.actions.save')
                      : t('pages.folders.actions.add')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {categoryToDelete && (
        <ConfirmationDialog
          open={!!categoryToDelete}
          onOpenChange={(open) => {
            if (!open && !isDeletingCategory) setCategoryToDelete(null);
          }}
          title={t('pages.categories.dialogs.delete.title')}
          description={t('pages.categories.dialogs.delete.description', {
            name: categoryToDelete?.name ?? '',
          })}
          confirmText={t('pages.categories.actions.delete')}
          cancelText={t('pages.categories.actions.cancel')}
          loadingText={t('pages.categories.actions.deleting')}
          variant="destructive"
          isLoading={isDeletingCategory}
          onConfirm={async () => {
            if (!categoryToDelete) return;
            setIsDeletingCategory(true);
            try {
              await deleteCategory(categoryToDelete.id);
              toast.success(successDeleteCategory);
              setCategoryToDelete(null);
              void loadData();
            } catch (err) {
              toast.error(
                err instanceof Error ? err.message : errorDeleteCategory,
              );
            } finally {
              setIsDeletingCategory(false);
            }
          }}
        />
      )}

      {foldersEnabled && deletingFolder && (
        <Dialog
          open={!!deletingFolder}
          onOpenChange={(open) => {
            if (!open && !deletingFolderAction) {
              setDeletingFolder(null);
              setDeleteMoveToFolderId('');
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t('pages.folders.dialogs.delete.title')}
              </DialogTitle>
              <DialogDescription>
                {t('pages.folders.dialogs.delete.description')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm font-medium">
                {t('pages.folders.dialogs.delete.moveOption')}
              </p>
              <div className="flex gap-2">
                <Select
                  value={deleteMoveToFolderId || ''}
                  onValueChange={setDeleteMoveToFolderId}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue
                      placeholder={t(
                        'pages.folders.dialogs.delete.selectDestination',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {flattenFolders(folders)
                      .filter((f) => f.id !== deletingFolder.id)
                      .map((n) => (
                        <SelectItem key={n.id} value={n.id}>
                          {'—'.repeat(n.level)} {n.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={() => handleDeleteFolder(false)}
                  disabled={!deleteMoveToFolderId || deletingFolderAction !== null}
                >
                  {deletingFolderAction === 'move' ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('pages.folders.dialogs.delete.moving')}
                    </>
                  ) : (
                    t('pages.folders.dialogs.delete.confirmMove')
                  )}
                </Button>
              </div>
              <p className="text-sm font-medium pt-2">
                {t('pages.folders.dialogs.delete.cascadeOption')}
              </p>
              <Button
                variant="destructive"
                onClick={() => handleDeleteFolder(true)}
                disabled={deletingFolderAction !== null}
              >
                {deletingFolderAction === 'delete' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('pages.folders.dialogs.delete.deleting')}
                  </>
                ) : (
                  t('pages.folders.dialogs.delete.confirmDelete')
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {foldersEnabled && movingNode && (
        <Dialog
          open={!!movingNode}
          onOpenChange={(open) => {
            if (!open) {
              setMovingNode(null);
              setMoveDestinationFolderId('');
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {movingNode.type === 'category'
                  ? t('pages.categories.dialogs.move.title', {
                      name: movingNode.name,
                    })
                  : t('pages.folders.dialogs.move.title', {
                      name: movingNode.name,
                    })}
              </DialogTitle>
              <DialogDescription>
                {movingNode.type === 'category'
                  ? t('pages.categories.dialogs.move.description')
                  : t('pages.folders.dialogs.move.description')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="move-destination">
                  {t('pages.folders.form.parentFolder')}
                </Label>
                <Select
                  value={moveDestinationFolderId || 'none'}
                  onValueChange={(v) =>
                    setMoveDestinationFolderId(v === 'none' ? '' : v)
                  }
                >
                  <SelectTrigger id="move-destination">
                    <SelectValue
                      placeholder={t('pages.folders.form.all')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {t('pages.folders.form.all')}
                    </SelectItem>
                    {(() => {
                      const flat = flattenFolders(folders);
                      const movingFolder =
                        movingNode.type === 'folder'
                          ? flat.find((f) => f.id === movingNode.id)
                          : null;
                      const excludeIds = movingFolder
                        ? [
                            movingFolder.id,
                            ...flattenFolders(
                              movingFolder.children || [],
                            ).map((c) => c.id),
                          ]
                        : [];
                      const options =
                        movingNode.type === 'category'
                          ? flat
                          : flat.filter((n) => !excludeIds.includes(n.id));
                      return options.map((n) => (
                        <SelectItem key={n.id} value={n.id}>
                          {'—'.repeat(n.level)} {n.name}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setMovingNode(null);
                  setMoveDestinationFolderId('');
                }}
              >
                {t('pages.categories.actions.cancel')}
              </Button>
              <Button onClick={() => void handleMoveConfirm()}>
                {t('pages.folders.actions.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {dndPendingMove && (
        <Dialog
          open={!!dndPendingMove}
          onOpenChange={(open) => {
            if (!open && !isDndConfirming) setDndPendingMove(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {dndPendingMove.item.type === 'category'
                  ? t('pages.categories.dialogs.move.confirm', {
                      item: dndPendingMove.item.name,
                      destination: dndPendingMove.destinationName,
                    })
                  : t('pages.folders.dialogs.move.confirm', {
                      item: dndPendingMove.item.name,
                      destination: dndPendingMove.destinationName,
                    })}
              </DialogTitle>
            </DialogHeader>
            {isDndConfirming ? (
              <div className="flex flex-col items-center gap-3 py-8">
                <LoadingLogo size="sm" />
                <span className="text-sm text-muted-foreground">
                  {t('pages.categories.actions.saving')}
                </span>
              </div>
            ) : (
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDndPendingMove(null)}
                >
                  {t('pages.categories.actions.cancel')}
                </Button>
                <Button onClick={() => void handleDndConfirm()}>
                  {t('pages.common.yes')}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {movingItem && (
        <Dialog
          open={!!movingItem}
          onOpenChange={(open) => {
            if (!open) {
              setMovingItem(null);
              setMoveDestinationCategoryId('');
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t('pages.items.dialogs.move.title', { name: movingItem.name })}
              </DialogTitle>
              <DialogDescription>
                {t('pages.items.dialogs.move.description')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="move-item-category">
                  {t('pages.items.table.category')}
                </Label>
                <Select
                  value={moveDestinationCategoryId || ''}
                  onValueChange={setMoveDestinationCategoryId}
                >
                  <SelectTrigger id="move-item-category">
                    <SelectValue
                      placeholder={t('pages.items.dialogs.move.description')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => {
                      const folderName = c.folderId
                        ? flattenFolders(folders).find(
                            (f) => f.id === c.folderId,
                          )?.name
                        : null;
                      const label = folderName
                        ? `${c.name} (${folderName})`
                        : c.name;
                      return (
                        <SelectItem key={c.id} value={c.id}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setMovingItem(null);
                  setMoveDestinationCategoryId('');
                }}
              >
                {t('pages.categories.actions.cancel')}
              </Button>
              <Button
                onClick={() => void handleItemMoveConfirm()}
                disabled={!moveDestinationCategoryId}
              >
                {t('pages.categories.actions.move')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {productMovePending && (
        <Dialog
          open={!!productMovePending}
          onOpenChange={(open) => {
            if (!open) setProductMovePending(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t('pages.items.dialogs.move.selectCategoryInFolder', {
                  folderName: productMovePending.folderName,
                })}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Select
                value=""
                onValueChange={(v) => {
                  if (v) {
                    void handleProductMovePendingConfirm(v);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t('pages.items.dialogs.move.description')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {categories
                    .filter((c) => c.folderId === productMovePending.folderId)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setProductMovePending(null)}
              >
                {t('pages.categories.actions.cancel')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
